import React, { useCallback } from 'react'

import { ExternalLink, Copy, PlusIcon, Eye, EyeOff } from 'lucide-react'
import { usePathname } from 'next/navigation'
import { useHotkeys } from 'react-hotkeys-hook'

import { SidebarLabel } from '~/components/Layout/AppSidebar'
import { useLastActivePath } from '~/components/Layout/useLastActivePath'
import { SplitsLink as Link } from '~/components/Link'
import { ShortenedEnsOrAddress } from '~/components/ShortenedEnsOrAddress'
import {
  Button,
  CurrencyAmount,
  Skeleton,
  Tooltip,
} from '~/components/component-library'
import { PasskeyConfirmationDialog } from '~/components/component-library/Dialog/PasskeyConfirmationDialog'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '~/components/ui/context-menu'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg, useActiveOrgId, useActiveOrgMember } from '~/hooks/org'
import { useArchiveSmartAccount } from '~/hooks/smart-accounts/useArchiveSmartAccount'
import { useOrgBalances } from '~/hooks/useBalances'
import { useNavigation } from '~/hooks/useNavigation'
import { useScrollEdgeFade } from '~/hooks/useScrollEdgeFade'
import { useShowArchivedAccounts } from '~/hooks/useShowArchivedAccounts'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import {
  OrgSmartAccount,
  PasskeyConfirmationAction,
  SmartAccountRole,
} from '~/types'
import { getExplorerAddressLinkChainId } from '~/utils/addresses'
import { copyToClipboard } from '~/utils/copyToClipboard'
import { getSmartAccountName } from '~/utils/smartAccounts'
import { cn } from '~/utils/tailwind-utils'

export const SmartAccountsSection = () => {
  const { org } = useActiveOrg()
  const { balances, isLoading: isLoadingBalances } = useOrgBalances(org?.id)
  const orgMember = useActiveOrgMember()
  const isOwner = orgMember?.role === 'OWNER'
  const [showArchived, setShowArchived] = useShowArchivedAccounts()
  const { containerProps, startOpacity, endOpacity } = useScrollEdgeFade({
    orientation: 'vertical',
  })

  const { smartAccounts: orderedSmartAccounts, isLoading } = useSmartAccounts({
    showArchived: isOwner,
    orderedByValue: true,
  })

  const activeAccounts = orderedSmartAccounts.filter(
    (account) => !account.isArchived,
  )
  const archivedAccounts = orderedSmartAccounts.filter(
    (account) => account.isArchived,
  )

  const allAccounts = [
    ...activeAccounts,
    ...(isOwner && showArchived ? archivedAccounts : []),
  ]
  const treasuryAccount = allAccounts.find(
    (a) => a.role === SmartAccountRole.TREASURY,
  )
  const subAccounts = allAccounts.filter(
    (a) => a.role !== SmartAccountRole.TREASURY,
  )

  useHotkeys(
    'meta+shift+period',
    () => {
      if (!isOwner) return
      setShowArchived(!showArchived)
    },
    { enabled: isOwner === true },
  )

  if (isLoading) return <div />

  return (
    <div className="relative flex min-h-0 w-full flex-1 flex-col">
      <div
        {...containerProps}
        className="min-h-0 w-full flex-1 overflow-y-auto px-2 pb-2"
      >
        {orderedSmartAccounts.length > 0 && (
          <>
            <SidebarLabel
              title="Accounts"
              className="sticky top-0 z-10 flex w-full flex-row items-center justify-between bg-gray-50 pt-2 dark:bg-gray-900"
            >
              {isOwner && (
                <div>
                  <Tooltip content={'Create account'}>
                    <Button
                      variant="tertiary"
                      size="small"
                      prefix={<PlusIcon className="text-secondary" />}
                      href={{ path: '/accounts/create' }}
                    />
                  </Tooltip>
                </div>
              )}
              {/* Top fade, pinned to the bottom edge of the sticky header so
                  items fade as they scroll up under "Accounts" */}
              <div
                aria-hidden
                style={{ opacity: startOpacity }}
                className="pointer-events-none absolute inset-x-0 top-full h-5 bg-gradient-to-b from-gray-50 to-transparent dark:from-gray-900"
              />
            </SidebarLabel>
            <div className="flex w-full flex-1 flex-col gap-0.5">
              {treasuryAccount && (
                <div className="pb-px">
                  <SmartAccountSidebarItem
                    balance={balances[treasuryAccount.address]?.totalValue ?? 0}
                    isLoadingBalance={isLoadingBalances}
                    smartAccount={treasuryAccount}
                  />
                </div>
              )}
              {subAccounts.length > 0 && (
                <div className="-mt-0.5 ml-[13px]">
                  {subAccounts.map((smartAccount, index) => (
                    <div
                      key={smartAccount.address}
                      className="relative py-px pl-2"
                    >
                      {index === subAccounts.length - 1 ? (
                        <div className="absolute left-0 top-0 h-[50%] w-1.5 rounded-bl border-b border-l border-gray-300 dark:border-gray-700" />
                      ) : (
                        <>
                          <div className="absolute left-0 top-0 h-full w-px bg-gray-300 dark:bg-gray-700" />
                          <div className="absolute left-0 top-[50%] h-px w-1.5 bg-gray-300 dark:bg-gray-700" />
                        </>
                      )}
                      <SmartAccountSidebarItem
                        balance={
                          balances[smartAccount.address]?.totalValue ?? 0
                        }
                        isLoadingBalance={isLoadingBalances}
                        smartAccount={smartAccount}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {/* Fades the list into the sidebar surface instead of cutting off
          abruptly; ramps in only once there's content below the fold. */}
      <div
        aria-hidden
        style={{ opacity: endOpacity }}
        className="pointer-events-none absolute inset-x-0 bottom-0 h-5 bg-gradient-to-t from-gray-50 to-transparent dark:from-gray-900"
      />
    </div>
  )
}

const SmartAccountSidebarItem = ({
  smartAccount,
  balance,
  isLoadingBalance,
}: {
  smartAccount: OrgSmartAccount
  balance: number
  isLoadingBalance: boolean
}) => {
  const { org } = useActiveOrg()
  const effectivePathname = useLastActivePath()
  const { setGlobalModalState, closeGlobalModal } = useAuth()

  const primaryChainId =
    smartAccount.chainIds?.[0] ?? org?.smartAccountsChainIds?.[0]

  const isActive = effectivePathname.startsWith(
    `/accounts/${smartAccount.address}`,
  )

  const canArchive =
    smartAccount.role !== SmartAccountRole.TREASURY &&
    smartAccount.role !== SmartAccountRole.ROOT

  const handleArchiveClick = () => {
    setGlobalModalState({
      isOpen: true,
      content: (
        <ArchiveSmartAccountDialogContent
          smartAccount={smartAccount}
          onClose={closeGlobalModal}
        />
      ),
    })
  }

  const handleArchivedClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.currentTarget.dispatchEvent(
      new MouseEvent('contextmenu', {
        bubbles: true,
        cancelable: true,
        clientX: e.clientX,
        clientY: e.clientY,
      }),
    )
  }

  return (
    <ContextMenu key={smartAccount.address + '-sidebar'}>
      <ContextMenuTrigger asChild>
        <Button
          variant="nav"
          size="small"
          fullWidth
          alignLeft
          isSelected={isActive}
          aria-current={isActive ? 'page' : undefined}
          href={
            smartAccount.isArchived
              ? undefined
              : { path: `/accounts/${smartAccount.address}` }
          }
          onClick={smartAccount.isArchived ? handleArchivedClick : undefined}
        >
          <div
            className={cn('flex w-full flex-col text-left', {
              'opacity-50': smartAccount.isArchived,
            })}
          >
            <span className="flex w-full items-center justify-between gap-2">
              <div className="flex-1 truncate whitespace-nowrap">
                {getSmartAccountName(smartAccount)}
              </div>
            </span>
            <div className="flex items-center justify-between gap-2 text-xs font-normal leading-5 text-secondary">
              <ShortenedEnsOrAddress address={smartAccount.address} />
              {isLoadingBalance ? (
                <Skeleton size="xs" length={2} />
              ) : (
                <div className="flex h-5 flex-shrink-0 items-center">
                  <CurrencyAmount value={balance} layout="inline" />
                </div>
              )}
            </div>
          </div>
        </Button>
      </ContextMenuTrigger>
      <ContextMenuContent
        className="w-48"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <ContextMenuItem
          className="flex space-x-3"
          onClick={() => {
            copyToClipboard(smartAccount.address, 'Address copied to clipboard')
          }}
        >
          <Copy className="h-4 w-4" />
          <div>Copy address</div>
        </ContextMenuItem>
        {primaryChainId && (
          <Link
            path={getExplorerAddressLinkChainId(
              smartAccount.address,
              primaryChainId,
            )}
            target="tab"
          >
            <ContextMenuItem className="flex space-x-3">
              <ExternalLink className="h-4 w-4" />
              <div>Open in explorer</div>
            </ContextMenuItem>
          </Link>
        )}
        {canArchive && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              className="flex space-x-3"
              onClick={handleArchiveClick}
            >
              {smartAccount.isArchived ? (
                <>
                  <Eye className="h-4 w-4" />
                  <div>Restore account</div>
                </>
              ) : (
                <>
                  <EyeOff className="h-4 w-4" />
                  <div>Archive account</div>
                </>
              )}
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  )
}

const ArchiveSmartAccountDialogContent = ({
  smartAccount,
  onClose,
}: {
  smartAccount: OrgSmartAccount
  onClose: () => void
}) => {
  const navigate = useNavigation()
  const pathname = usePathname()
  const activeOrgId = useActiveOrgId()

  const isArchived = smartAccount.isArchived
  const accountName = getSmartAccountName(smartAccount).replace(
    / \[ARCHIVED\]$/,
    '',
  )

  const { archiveSmartAccount, unarchiveSmartAccount } =
    useArchiveSmartAccount(smartAccount)

  const handleConfirmed = useCallback(
    async (confirmationId: string) => {
      const action = isArchived ? unarchiveSmartAccount : archiveSmartAccount

      await action({
        confirmationId,
        onSuccess: () => {
          if (isArchived) {
            navigate(`/accounts/${smartAccount.address}/`)
          } else if (pathname.startsWith(`/accounts/${smartAccount.address}`)) {
            navigate('/')
          }
        },
      })
    },
    [
      isArchived,
      archiveSmartAccount,
      unarchiveSmartAccount,
      smartAccount.address,
      navigate,
      pathname,
    ],
  )

  return (
    <PasskeyConfirmationDialog
      id="archiveSmartAccount"
      title={`${isArchived ? 'Restore' : 'Archive'} "${accountName}"?`}
      confirmText={isArchived ? 'Restore' : 'Archive'}
      confirmation={{
        action: isArchived
          ? PasskeyConfirmationAction.SmartAccountUnarchive
          : PasskeyConfirmationAction.SmartAccountArchive,
        orgId: activeOrgId,
        resourceId: smartAccount.id,
      }}
      onConfirmed={handleConfirmed}
      onClose={onClose}
    />
  )
}
